import { useCallback, type ReactNode } from "react";
import { User } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GooglePlaceAutocomplete } from "@/components/ui/customs/autocomplete";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { isEuropeanUnionCountry } from "@/constants/countries";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { ExpeditionBoDeliveryInfoFormData } from "@/types/forms/bo/expeditionBo.form";
import { DeliveryInfoProps } from "../../../expeditionForm.types";
import { FedexAddressValidationActions } from "../../fedexAddressValidation/fedexAddressValidationActions";
import { ContactInformations } from "./contactInformations";
import { CustomsInformation } from "./customsInformation";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

type DeliveryInfoFieldsProps = {
  countryCode: string;
  addressFieldsDisabled?: boolean;
  beforeAddressSection?: ReactNode;
  afterAddressSection?: ReactNode;
};

const DeliveryInfoFields = ({
  countryCode,
  addressFieldsDisabled = false,
  beforeAddressSection,
  afterAddressSection,
}: DeliveryInfoFieldsProps) => {
  const translateMessage = useTranslateMessage();
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<ExpeditionBoDeliveryInfoFormData>();

  const showStateOrProvince = countryCode === "CA" || countryCode === "US";
  const isUE = isEuropeanUnionCountry(countryCode);
  const showPostalCode = countryRequiresPostalCode(countryCode);

  const handlePlaceSelect = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      const parsed = parseGooglePlaceAddress(place, countryCode);
      if (!parsed) return;

      methods.setValue(`deliveryInfo.street`, parsed.street);
      methods.setValue(`deliveryInfo.city`, parsed.city);
      methods.setValue(`deliveryInfo.postalCode`, parsed.postalCode);
      if (showStateOrProvince) {
        methods.setValue(`deliveryInfo.stateOrProvinceCode`, parsed.stateOrProvinceCode);
      }
    },
    [methods, countryCode, showStateOrProvince],
  );

  return (
    <AccordionItem
      value="deliveryInfo"
      className="shadow-basic flex w-full flex-col items-start justify-center gap-4 rounded-xl border border-gray-200 bg-slate-100 p-6"
    >
      <AccordionTrigger className="flex w-full flex-row items-start justify-between gap-2 self-start !no-underline">
        <div className="flex flex-row items-center gap-2 self-start">
          <User className="h-6 w-6 !rotate-0 text-blue-600" />
          <p className="text-xl font-bold">{t("deliveryInfo.title")}</p>
        </div>
      </AccordionTrigger>
      <AccordionContent forceMount className="flex w-full flex-col gap-2">
        <div className="flex w-full flex-col gap-2">
          <ControlledUnifiedInput
            control={methods.control}
            name="deliveryInfo.company"
            label={t("deliveryInfo.companyLabel")}
            className="w-full"
            wrapperClassName="w-full"
          />
          <ControlledCheckbox
            control={methods.control}
            name="options.includeCompanyName"
            label={t("deliveryInfo.includeCompanyNameLabel")}
          />
        </div>
        <div className="flex flex-col gap-2 @xl:flex-row">
          <ControlledUnifiedInput
            control={methods.control}
            name="deliveryInfo.firstName"
            label={t("deliveryInfo.firstNameLabel")}
            required
            className="w-full"
            wrapperClassName="w-full"
          />
          <ControlledUnifiedInput
            control={methods.control}
            name="deliveryInfo.lastName"
            label={t("deliveryInfo.lastNameLabel")}
            required
            className="w-full"
            wrapperClassName="w-full"
          />
        </div>
        <ContactInformations countryCode={countryCode} />
        <div className="my-2 w-full border-t-2 border-dashed border-slate-300" />
        {beforeAddressSection}
        <div className="flex w-full flex-row items-start gap-2">
          <Controller
            name="deliveryInfo.street"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <GooglePlaceAutocomplete
                label={t("deliveryInfo.streetLabel")}
                required
                value={field.value || ""}
                onInputChange={(val) => field.onChange(val)}
                onPlaceSelect={handlePlaceSelect}
                error={translateMessage(error?.message)}
                wrapperClassName="w-full min-w-0"
                countryRestrictions={countryCode ? [countryCode] : undefined}
                disabled={addressFieldsDisabled}
              />
            )}
          />
          <div className="flex h-14 shrink-0 flex-col">
            <FedexAddressValidationActions variant="delivery" />
          </div>
        </div>
        <div className="flex flex-col gap-2 @xl:flex-row">
          <ControlledUnifiedInput
            control={methods.control}
            name="deliveryInfo.additionalInfo"
            label={t("deliveryInfo.additionalInfoLabel")}
            className="w-full"
            wrapperClassName="w-full"
            disabled={addressFieldsDisabled}
          />
          <ControlledUnifiedInput
            control={methods.control}
            name="deliveryInfo.additionalInfo2"
            label={t("deliveryInfo.additionalInfo2Label")}
            className="w-full"
            wrapperClassName="w-full"
            disabled={addressFieldsDisabled}
          />
        </div>
        <div className="flex flex-col gap-2 @xl:flex-row">
          {showPostalCode && (
            <ControlledUnifiedInput
              className="w-full"
              control={methods.control}
              name="deliveryInfo.postalCode"
              label={t("deliveryInfo.postalCodeLabel")}
              required
              wrapperClassName="w-full"
              disabled={addressFieldsDisabled}
            />
          )}
          <ControlledUnifiedInput
            className="flex-1"
            control={methods.control}
            name="deliveryInfo.city"
            label={t("deliveryInfo.cityLabel")}
            required
            wrapperClassName="w-full"
            disabled={addressFieldsDisabled}
          />
          {showStateOrProvince && (
            <ControlledUnifiedInput
              className="w-full"
              control={methods.control}
              name="deliveryInfo.stateOrProvinceCode"
              label={t("deliveryInfo.stateOrProvinceLabel")}
              required
              wrapperClassName="w-full"
              disabled={addressFieldsDisabled}
            />
          )}
        </div>
        {afterAddressSection}
        {/* In EU those informations are not needed */}
        {!isUE && (
          <>
            <div className="my-2 w-full border-t-2 border-dashed border-slate-300" />
            <h3 className="text-lg font-bold">{t("deliveryInfo.customsInformationTitle")}</h3>
            <CustomsInformation />
          </>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

export const ExpeditionDeliveryInfo = ({ expedition, custom }: DeliveryInfoProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<ExpeditionBoDeliveryInfoFormData>();
  const countryCode = expedition.deliveryInfo?.country ?? "";

  return (
    <DeliveryInfoFields
      countryCode={countryCode}
      afterAddressSection={
        <ControlledSelectV2
          control={methods.control}
          name="customId"
          label={t("deliveryInfo.customPolicyLabel")}
          isDisabled={true}
          options={
            custom
              ? [
                  {
                    value: custom.id,
                    label: custom.label,
                  },
                ]
              : []
          }
        />
      }
    />
  );
};
